<template>
  <Modal
    v-if="item.tempCommand"
    :value="value"
    @hide="hide()"
    :title="item.chocoPackage.Title"
    :subtitle="`${nameAndVersion.name} - ${nameAndVersion.version}`"
  >
    <div slot="body" class="content">
      <div v-if="isPseudo" class="notification is-warning">
        This package does not exist in the repo it originated from, therefore
        only uninstallation and delete from database is possible. Uninstallation
        is needed before deletion, since deletion would remove the abillity to
        controll the package.
      </div>

      <div v-if="disabled" class="notification is-info">
        This package is present in another group. Delete the package from the
        other group, or configure the package in that group.
        <ul>
          <li
            v-for="item of dupPackages"
            :key="item.version.packageHash + item.source.uid"
          >
            <router-link
              :to="
                `/domains/${$route.params.domainUID}/groups/${item.group.uid}/packages`
              "
              >{{ item.group.name }}</router-link
            >
          </li>
        </ul>
      </div>

      <section v-if="!disabled">
        <GroupWarnings :item="item"></GroupWarnings>
        <Schedule
          :schedule="item.schedule"
          @changed="setTempSchedule($event)"
        ></Schedule>
        <Switches :isGroup="true" :item="item"></Switches>
        <Parameters :isGroup="true" :item="item"></Parameters>
      </section>
    </div>

    <template slot="footer" class="content">
      <div v-if="!isLoading">
        <div
          v-if="
            !disabled &&
              !isPseudo &&
              (isUpgradable || isAutoUpgrade) &&
              !hasParam('-m')
          "
          :disabled="hasMultiple || higherVersionInstalled || hasParam('-m')"
          class="button is-info"
          @click="sendPackage(item, 'upgrade')"
        >
          Upgrade
        </div>
        <div
          v-if="!isPseudo && !disabled"
          :disabled="
            ((hasMultiple || higherVersionInstalled) && !hasParam('-m')) ||
              isTempAutoUpgrade ||
              (hasAutoUpgrade && !isAutoUpgrade)
          "
          class="button is-success"
          @click="sendPackage(item, 'install')"
        >
          Install
        </div>
        <div
          v-if="!disabled"
          :disabled="(hasAutoUpgrade && !isAutoUpgrade) || isTempAutoUpgrade"
          class="button is-danger"
          @click="sendPackage(item, 'uninstall')"
        >
          Uninstall
        </div>

        <div
          :disabled="
            !item.command || !item.command.name || (isPseudo && isUninstallable)
          "
          class="button is-warning"
          @click="
            !item.command || !item.command.name || (isPseudo && isUninstallable)
              ? null
              : deletePackage()
          "
        >
          Delete
        </div>
        <div class="button" @click="hide()">Cancel</div>
      </div>
      <div v-show="isLoading" class="spinner-wrapper center">
        <span class="spinner is-centered"></span>
      </div>
    </template>
  </Modal>
</template>

<script>
import { getPackageNameVersionAndHash, hasParam } from "@/utils/mdc";
import {
  formatPackage,
  addTempParam,
  getAllMdcVersions,
  getAllMdcVersionsFn,
} from "@/utils/packages";
import { sendPackages, cancelPackageSchedule } from "@/utils/socket";
import status from "@/utils/groupPackageStatus";
import { postApi } from "@/utils/http";
import { isScheduleValid } from "@/utils/packageDate";

export default {
  props: { value: Boolean, item: Object },
  components: {
    GroupWarnings: () => import("@/components/main/packages/GroupWarnings.vue"),
    Switches: () => import("@/components/main/packages/Switches.vue"),
    Modal: () => import("./Modal"),
    Schedule: () => import("./Schedule"),
    Parameters: () => import("@/components/main/packages/Parameters.vue"),
  },
  data() {
    return {
      isLoading: false,
      dupPackages: [],
    };
  },
  computed: {
    disabled() {
      return this.dupPackages.length > 0;
    },
    isPseudo() {
      return this.item?.chocoPackage?.Pseudo;
    },

    hasParam() {
      return (param) => {
        return hasParam(this.item.tempCommand.parameters, param);
      };
    },
    higherVersionInstalled() {
      return status.isHigherVersionInstalled(this.item);
    },
    hasMultiple() {
      return status.hasMultipleVersions(this.item);
    },
    nameAndVersion() {
      return getPackageNameVersionAndHash(this.item);
    },
    isUpgradable() {
      return status.isUpgradable(this.item);
    },
    isInstallable() {
      return status.isInstallable(this.item);
    },
    isUninstallable() {
      return status.isUninstallable(this.item);
    },
    hasValidSchedule() {
      return isScheduleValid(this.item.tempSchedule);
    },
    isTempAutoUpgrade() {
      return status.isTempAutoUpgrade(this.item);
    },
    isAutoUpgrade() {
      return status.isAutoUpgrade(this.item);
    },
    hasAutoUpgrade() {
      return status.hasAutoUpgrade(this.item);
    },
  },
  async created() {
    this.isLoading = true;

    if (this.item) {
      const { name, uid } = this.nameAndVersion;
      const shouldReload = !this.$store.state.groupPackage.fullyLoadedPackages[
        name + uid
      ];

      if (shouldReload) {
        const mdcPackages = await getAllMdcVersions(
          this.item,
          "groupPackage",
          getAllMdcVersionsFn(this.item, true, this.$route.params)
        );

        this.$store.commit(
          `groupPackage/setMergedPackageVersions`,
          mdcPackages
        );
      }

      this.checkDups(name);

      this.$store.commit("groupPackage/editMergedPackage", {
        p: this.item,
        obj: { tempCommand: { ...this.item.command } },
      });
    }

    this.isLoading = false;
  },
  methods: {
    async checkDups() {
      const { name, uid } = this.nameAndVersion;

      const result = await this.$store.dispatch(
        "groupPackage/getDuplicateDomainPackages",
        {
          name,
          domainUID: this.$route.params.domainUID,
        }
      );

      if (result && result[0] && result[0].length > 0) {
        this.dupPackages = result[0].filter(
          (x) => x.group.uid !== this.$route.params.groupUID
        );
      }
    },
    validate(action) {
      if (
        action === "upgrade" &&
        (this.hasMultiple || this.higherVersionInstalled || hasParam("-m"))
      ) {
        return false;
      } else if (
        action === "install" &&
        (((this.hasMultiple || this.higherVersionInstalled) &&
          !this.hasParam("-m")) ||
          this.isTempAutoUpgrade ||
          (this.hasAutoUpgrade && !this.isAutoUpgrade))
      ) {
        return false;
      } else if (
        action === "uninstall" &&
        ((this.hasAutoUpgrade && !this.isAutoUpgrade) || this.isTempAutoUpgrade)
      ) {
        return false;
      }

      return true;
    },
    async sendPackage(item, action) {
      if (this.validate(action)) {
        this.isLoading = true;

        if (status.isTempAutoUpgrade(item) && hasParam("-m")) {
          item.tempCommand.parameters = addTempParam(
            item.tempCommand.parameters,
            "-m"
          );
        }

        this.$emit("action", action);

        const newItem = formatPackage(item, action);
        newItem.command.override = false;

        if (
          item.schedule &&
          item.schedule.type === 1 &&
          ((item.tempSchedule && item.tempSchedule.type !== 1) ||
            !item.tempSchedule)
        ) {
          await this.$store.dispatch("group/fetch", {
            domainUID: this.$route.params.domainUID,
          });

          const { name, version, hash } = getPackageNameVersionAndHash(
            this.item
          );
          const computerUIDs = this.$store.getters["group/computerUIDs"](
            this.$route.params.groupUID
          );

          cancelPackageSchedule(
            [
              {
                package: { name },
                version: { version, packageHash: hash },
              },
            ],
            computerUIDs
          );

          await this.$helpers.wait(2);
        }

        if (sendPackages([newItem], [], this.$route.params.groupUID)) {
          this.$store.commit("groupPackage/mergeMdcPackage", newItem);

          this.$store.commit("groupPackage/editMergedPackage", {
            p: item,
            obj: { command: newItem.command, schedule: newItem.schedule },
          });
        } else {
          this.$helpers.notify(
            "Error",
            "Could not send the package.",
            "danger"
          );
        }

        this.hide();
      }
    },
    async deletePackage() {
      this.isLoading = true;
      const { name, version, hash } = getPackageNameVersionAndHash(this.item);

      await postApi("delete/groupPackage", {
        groupUID: this.$route.params.groupUID,
        packageHash: hash,
        domainUID: this.$route.params.domainUID,
      });

      await this.$store.dispatch("group/fetch", {
        domainUID: this.$route.params.domainUID,
      });

      const computerUIDs = this.$store.getters["group/computerUIDs"](
        this.$route.params.groupUID
      );

      cancelPackageSchedule(
        [
          {
            package: { name },
            version: { version, packageHash: hash },
          },
        ],
        computerUIDs
      );

      this.$store.commit("groupPackage/editMergedPackage", {
        p: this.item,
        obj: {
          schedule: {},
          package: {},
          version: {},
          command: {},
        },
      });
      this.$store.commit("groupPackage/removeMdcPackage", this.item);

      this.hide();
    },
    setTempSchedule(newSchedule) {
      this.$store.commit("groupPackage/editMergedPackage", {
        p: this.item,
        obj: { tempSchedule: newSchedule },
      });
    },
    hide() {
      this.$store.commit("groupPackage/editMergedPackage", {
        p: this.item,
        obj: { tempCommand: null },
      });

      this.$emit("hide");
      this.$emit("input", false);
    },
  },
};
</script>
